// src/hooks/useLivePoll.ts
//
// A polling effect that does not run when nobody is looking, and does not stack
// requests on top of each other.
//
// WHY THIS EXISTS (2026-08-26). The Signals page polled `risk/long_end_vol` every 30s
// with a bare `setInterval` and no guards. Two consequences, both measured in production:
//
//   1. A dashboard tab left open overnight kept polling. Outside regular trading hours
//      those endpoints cannot resolve a live quote, so each call grinds its IBKR retry
//      ladder to exhaustion — measured p50 117,380 ms per call, ~1,130 calls between
//      22:00 and 08:00 MT. That single hidden tab was the largest source of IBKR rate
//      limiting we had: 84% of all HTTP 429s traced back to this one endpoint.
//   2. With a 30s interval against a ~117s response, ticks overlapped. We measured 177
//      calls/hour where one tab at 30s should produce 120 — the page was queueing
//      requests behind requests and then waiting in the queue it had built.
//
// IBKR documents a 10-minute penalty box for rate-limit violations and a permanent
// block for repeat offenders, and all our traffic leaves via one allowlisted NAT IP, so
// "poll harder when things are slow" is not a survivable failure mode.
//
// This hook is display-plumbing only. It changes WHEN data is requested, never what is
// requested or what is shown. While the tab is visible the cadence is unchanged, so no
// displayed number goes staler than before for anyone actually looking at it.

import { useEffect, useRef } from "react";

type UseLivePollOptions = {
  /** When false the poll is inert (e.g. while auth is still resolving). Default true. */
  enabled?: boolean;
  /**
   * Fire once immediately when the tab becomes visible again. Default true.
   * Without this, returning to a backgrounded tab would show data frozen at the moment
   * you left it for up to a full interval — stale data presented as current, which is
   * the failure mode this dashboard exists to avoid.
   */
  refreshOnVisible?: boolean;
  /**
   * Run once on mount. Default true. Pass false where a separate mount effect already
   * performs the initial load, so converting an existing `setInterval` to this hook does
   * not silently add a duplicate fetch on every page open.
   */
  immediate?: boolean;
};

/**
 * Run `load` immediately, then every `intervalMs`, but only while the document is
 * visible and only when the previous run has finished.
 *
 * `load` is held in a ref, so it may close over fresh state without restarting the
 * interval. It is responsible for its own error handling — a rejection is swallowed
 * here so one bad response cannot kill the timer (callers keep last-good values).
 *
 * React 18 makes setState-after-unmount a no-op, so callers do not need the old
 * `let alive = true` guard; the interval and listener are removed on unmount.
 */
export function useLivePoll(
  load: () => Promise<unknown> | unknown,
  intervalMs: number,
  options: UseLivePollOptions = {},
): void {
  const { enabled = true, refreshOnVisible = true, immediate = true } = options;

  const loadRef = useRef(load);
  loadRef.current = load;

  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    const run = async () => {
      // Nobody is looking — do not spend an IBKR call, or our rate-limit budget, on a
      // number no one can see. This is the guard that ends the overnight storm.
      if (typeof document !== "undefined" && document.hidden) return;

      // A tick arrived while the previous request is still outstanding. Skipping is
      // strictly better than queueing: the in-flight response is at least as fresh as
      // anything a second request would return, and stacking is what turned a slow
      // endpoint into a self-inflicted rate limit.
      if (inFlightRef.current) return;

      inFlightRef.current = true;
      try {
        await loadRef.current();
      } catch {
        /* caller owns its own failure handling; never let one error stop the timer */
      } finally {
        inFlightRef.current = false;
      }
    };

    if (immediate) void run();
    const id = window.setInterval(run, intervalMs);

    const onVisibilityChange = () => {
      if (refreshOnVisible && !document.hidden) void run();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [enabled, intervalMs, refreshOnVisible, immediate]);
}

export default useLivePoll;
